import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { APP } from '@/constants/testIds';
import { toast } from 'sonner';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';

const sevPill = { low: 'pill-low', medium: 'pill-medium', high: 'pill-high', critical: 'pill-critical' };

const Alerts = () => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => { setLoading(true); const { data } = await api.get('/alerts'); setAlerts(data); setLoading(false); };
  useEffect(() => { load(); }, []);

  const resolve = async (id) => { await api.post(`/alerts/${id}/resolve`); toast.success('Alert resolved'); load(); };

  const open = alerts.filter((a) => !a.resolved);
  const resolved = alerts.filter((a) => a.resolved);

  return (
    <div className="space-y-8">
      <header>
        <div className="label-tag mb-2">// Security Alerts</div>
        <h1 className="text-4xl md:text-5xl font-light tracking-tight">Threat feed</h1>
        <p className="text-[color:var(--text-secondary)] mt-2 max-w-2xl">
          Triage incidents by severity. Resolved alerts are archived but always auditable.
        </p>
      </header>

      <section className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-[color:var(--border)]">
        <div className="bg-[color:var(--bg-base)] p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="label-tag">// Open · {open.length}</div>
            <AlertTriangle size={14} className="text-[color:var(--status-high)]" />
          </div>
          {loading ? <Loading /> : open.length === 0 ? <Empty>No open alerts. Network is calm.</Empty> : (
            <ul className="space-y-3">
              {open.map((a) => (
                <li key={a.alert_id} data-testid={APP.alertItem} className="panel-soft p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`pill ${sevPill[a.severity]}`}>{a.severity}</span>
                    <span className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">{a.alert_type.replace(/_/g, ' ')}</span>
                    <span className="ml-auto font-mono text-[10px] text-[color:var(--text-muted)]">{new Date(a.created_at).toLocaleString()}</span>
                  </div>
                  <div className="text-base mb-1">{a.title}</div>
                  <div className="text-sm text-[color:var(--text-secondary)] mb-3">{a.message}</div>
                  <button data-testid={APP.alertResolveBtn} onClick={() => resolve(a.alert_id)} className="btn-secondary !py-2 !text-[10px]">
                    <CheckCircle2 size={12} /> Resolve
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bg-[color:var(--bg-base)] p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="label-tag">// Resolved · {resolved.length}</div>
            <CheckCircle2 size={14} className="text-[color:var(--status-low)]" />
          </div>
          {loading ? <Loading /> : resolved.length === 0 ? <Empty>No resolved alerts yet.</Empty> : (
            <ul className="space-y-2">
              {resolved.slice(0, 20).map((a) => (
                <li key={a.alert_id} className="text-[color:var(--text-secondary)] text-sm flex justify-between border-b border-[color:var(--border-soft)] py-2">
                  <span>{a.title}</span>
                  <span className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">{a.severity}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
    </div>
  );
};
const Loading = () => <div className="font-mono text-xs uppercase tracking-widest text-[color:var(--text-muted)] blink py-4">Loading…</div>;
const Empty = ({ children }) => <div className="font-mono text-xs text-[color:var(--text-secondary)] py-4">{children}</div>;
export default Alerts;
